import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Link from "@mui/material/Link";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import EntityTable, { StatusBadge, RowActionMenu } from "components/EntityTable";
import ConfirmDialog from "components/ConfirmDialog";
import useWebsitesTable from "hooks/useWebsitesTable";

import { formatRelative } from "utils/datetime";

import { WebsiteDialog } from "./components/UI/WebsiteDialog";

const slugify = (name) => name.toLowerCase().replace(/\s+/g, "-");

// Standalone Websites listing — a paginated table of every website.
function WebsiteList() {
  const navigate = useNavigate();
  const websites = useWebsitesTable();

  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ open: false, website: null });
  const [confirm, setConfirm] = useState({ open: false, website: null });
  const [submitting, setSubmitting] = useState(false);

  const openCreate = () => setForm({ open: true, website: null });
  const openEdit = (website) => setForm({ open: true, website });
  const closeForm = () => setForm({ open: false, website: null });

  const goToDetail = (website) =>
    navigate(`/websites/${slugify(website.name)}`, {
      state: { websiteId: website.website_id },
    });

  const toggleActive = (website) =>
    website.active
      ? websites.deactivate(website.website_id)
      : websites.activate(website.website_id);

  const handleSubmit = async (data) => {
    setSubmitting(true);
    try {
      if (form.website) {
        await websites.update(form.website.website_id, {
          name: data.name,
          domain: data.domain,
        });
      } else {
        await websites.create({ name: data.name, domain: data.domain });
      }
      closeForm();
    } catch {
      // useWebsitesTable already surfaced the error via snackbar.
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await websites.remove(confirm.website.website_id);
      setConfirm({ open: false, website: null });
    } catch {
      // useWebsitesTable already surfaced the error via snackbar.
    } finally {
      setSubmitting(false);
    }
  };

  // Client-side filter of the loaded page (the list endpoint has no search param).
  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return websites.rows;
    return websites.rows.filter(
      (w) => w.name?.toLowerCase().includes(query) || w.domain?.toLowerCase().includes(query)
    );
  }, [websites.rows, search]);

  const columns = [
    { field: "name", headerName: "Website", flex: 1, minWidth: 160 },
    {
      field: "domain",
      headerName: "URL",
      flex: 1.6,
      minWidth: 220,
      renderCell: ({ value }) => (
        <Link
          href={`https://${value}`}
          target="_blank"
          rel="noreferrer"
          onClick={(event) => event.stopPropagation()}
        >
          {value}
        </Link>
      ),
    },
    {
      field: "updated_at",
      headerName: "Last edited",
      flex: 0.9,
      minWidth: 140,
      renderCell: ({ value }) => formatRelative(value),
    },
    {
      field: "active",
      headerName: "Status",
      flex: 0.7,
      minWidth: 120,
      renderCell: ({ value }) => <StatusBadge active={value} />,
    },
    {
      field: "actions",
      headerName: "",
      width: 64,
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <RowActionMenu
          actions={[
            { label: "Edit", icon: "edit", onClick: () => openEdit(row) },
            {
              label: row.active ? "Deactivate" : "Activate",
              icon: row.active ? "block" : "check_circle",
              onClick: () => toggleActive(row),
            },
            {
              label: "Delete",
              icon: "delete",
              color: "error",
              onClick: () => setConfirm({ open: true, website: row }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          mb={3}
        >
          <MDTypography variant="h4" fontWeight="bold">
            Websites
          </MDTypography>
          <MDButton
            variant="gradient"
            color="info"
            startIcon={<Icon>add</Icon>}
            onClick={openCreate}
          >
            Add Website
          </MDButton>
        </MDBox>

        <Card>
          <MDBox p={3}>
            <MDBox mb={2} maxWidth={320}>
              <MDInput
                fullWidth
                placeholder="Search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                InputProps={{
                  endAdornment: <Icon fontSize="small">search</Icon>,
                }}
              />
            </MDBox>
            <EntityTable
              rows={filteredRows}
              columns={columns}
              getRowId={(row) => row.website_id}
              loading={websites.loading}
              rowCount={websites.total}
              page={websites.page}
              pageSize={websites.pageSize}
              onPageChange={websites.setPage}
              onPageSizeChange={websites.setPageSize}
              onRowClick={({ row }) => goToDetail(row)}
            />
          </MDBox>
        </Card>
      </MDBox>

      <WebsiteDialog
        open={form.open}
        website={form.website}
        onClose={closeForm}
        onSubmit={handleSubmit}
        isLoading={submitting}
      />

      <ConfirmDialog
        open={confirm.open}
        title="Delete Website"
        message={`Delete website "${confirm.website?.name || ""}"? This cannot be undone.`}
        confirmText="Delete"
        loading={submitting}
        onConfirm={handleDelete}
        onClose={() => setConfirm({ open: false, website: null })}
      />
    </DashboardLayout>
  );
}

export default WebsiteList;
